import { Component, ElementRef, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface UsuarioSeleccionable {
  id: number;
  name: string;
  email: string;
}

/**
 * Selector de usuario con búsqueda escribiendo.
 *
 * Un `<select>` nativo obliga a recorrer una lista larga a ojo; aquí se escribe parte
 * del nombre o del correo y la lista se filtra. Implementa ControlValueAccessor, así
 * que se usa igual que cualquier control de un formulario reactivo.
 *
 * La lista que reciba ya debe venir filtrada por el backend (solo usuarios activos):
 * este componente no decide quién es elegible, solo cómo se busca.
 */
@Component({
  selector: 'app-user-picker',
  standalone: false,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => UserPickerComponent), multi: true },
  ],
  templateUrl: './user-picker.component.html',
})
export class UserPickerComponent implements ControlValueAccessor {
  @Input() usuarios: UsuarioSeleccionable[] = [];
  @Input() placeholder = 'Buscar por nombre o correo…';
  /** Texto de la opción que representa "ninguno" / el valor por defecto. */
  @Input() etiquetaVacio = 'Sin asignar';
  /** Si es false, no se ofrece la opción vacía. */
  @Input() permiteVacio = true;
  @Input() error?: string;

  abierto = false;
  filtro = '';
  resaltado = -1;

  valor: number | null = null;
  disabled = false;

  private onChange: (v: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  // ── ControlValueAccessor ────────────────────────────────────────────────
  writeValue(v: number | null): void {
    this.valor = v ?? null;
  }
  registerOnChange(fn: (v: number | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ── Presentación ────────────────────────────────────────────────────────
  get seleccionado(): UsuarioSeleccionable | undefined {
    return this.usuarios.find((u) => u.id === this.valor);
  }

  get textoBoton(): string {
    return this.seleccionado ? this.seleccionado.name : this.etiquetaVacio;
  }

  get filtrados(): UsuarioSeleccionable[] {
    const q = this.filtro.trim().toLowerCase();
    if (!q) return this.usuarios;
    return this.usuarios.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }

  // ── Interacción ─────────────────────────────────────────────────────────
  alternar(): void {
    if (this.disabled) return;
    this.abierto = !this.abierto;
    if (this.abierto) {
      this.filtro = '';
      this.resaltado = -1;
      // El input aún no existe en el DOM cuando se abre: se enfoca en el siguiente ciclo.
      setTimeout(() => this.host.nativeElement.querySelector<HTMLInputElement>('[data-buscador]')?.focus());
    } else {
      this.onTouched();
    }
  }

  elegir(id: number | null): void {
    this.valor = id;
    this.onChange(id);
    this.onTouched();
    this.abierto = false;
  }

  /** Flechas para recorrer, Enter para elegir, Escape para cerrar. */
  teclado(evento: KeyboardEvent): void {
    const lista = this.filtrados;

    if (evento.key === 'Escape') {
      this.abierto = false;
      this.onTouched();
      return;
    }
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault();
      if (!lista.length) return;
      const paso = evento.key === 'ArrowDown' ? 1 : -1;
      this.resaltado = (this.resaltado + paso + lista.length) % lista.length;
      return;
    }
    if (evento.key === 'Enter') {
      evento.preventDefault();
      // Sin nada resaltado pero con un único resultado, Enter elige ese.
      const elegido = this.resaltado >= 0 ? lista[this.resaltado] : lista.length === 1 ? lista[0] : undefined;
      if (elegido) this.elegir(elegido.id);
    }
  }

  /** Cerrar al hacer clic fuera; si no, el desplegable se queda abierto por la página. */
  @HostListener('document:pointerdown', ['$event'])
  clicFuera(evento: Event): void {
    if (!this.abierto) return;
    if (!this.host.nativeElement.contains(evento.target as Node)) {
      this.abierto = false;
      this.onTouched();
    }
  }

  iniciales(nombre: string): string {
    return nombre
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase();
  }
}
